/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import { Box } from "@mui/material";
import React, { useState, useMemo } from "react";
import { css } from "aphrodite";
import DropDownSelect from "../../components/common/Dropdown/index";
import {
  MONTHS_OPTIONS,
  YEARS_OPTIONS,
  getCurrentMonth,
  getPreviousMonth,
  getCurrentYear,
} from "../../utils/dateAndTime";
import Typography from "../../components/common/new/Typography/Typography";
import InputField from "../../components/common/new/InputField/InputField";
import Colors from "../../theme/colors";
import Styles from "./styles";
import { isEmptyObj } from "../../utils/helper";

// ✅ Proper Tile Design: No squishing, no text wrapping
function StatTile({ label, present, total, bg, textColor }) {
  return (
    <Box
      sx={{
        background: bg,
        borderRadius: "6px",
        padding: "4px 10px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        whiteSpace: "nowrap",
        flexShrink: 0, // TILE KABHI DABAYGI NAHI
      }}
    >
      <Typography
        sx={{
          color: textColor,
          fontSize: "11px",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: textColor,
          fontSize: "12px",
          fontWeight: 700,
        }}
      >
        {present}/{total}
      </Typography>
    </Box>
  );
}

function AttendanceHeader({
  isArchive,
  attendanceList,
  meetings,
  otherAttendeesCount,
  onChangeOtherAttendeesCount,
  selectedMeetingType,
}) {
  const [month, setMonth] = useState(() =>
    isArchive ? getPreviousMonth(getCurrentMonth()) : getCurrentMonth(true)
  );
  const [year, setYear] = useState(getCurrentYear());

  const isGivenIdPresent = (id) => attendanceList.includes(id);

  const [presentFriends, presentLeaders, presentMembers, presentNewFriends] =
    useMemo(() => {
      if (meetings && !isEmptyObj(meetings)) {
        return [
          meetings?.friends?.length
            ? meetings?.friends.filter((item) =>
                isGivenIdPresent(item.friendId)
              ).length
            : 0,
          meetings?.leaders?.length
            ? meetings?.leaders.filter((item) =>
                isGivenIdPresent(`${item.memberId}-L`)
              ).length
            : 0,
          meetings?.members?.length
            ? meetings?.members.filter((item) =>
                isGivenIdPresent(item.memberId)
              ).length
            : 0,
          meetings?.newFriends?.length
            ? meetings?.newFriends.filter((item) =>
                isGivenIdPresent(item.friendId)
              ).length
            : 0,
        ];
      }
      return [0, 0, 0, 0];
    }, [meetings, attendanceList]);

  const totalFriendsCount = meetings?.friends?.length ?? 0;
  const totalMembersCount = meetings?.members?.length ?? 0;
  const totalNewFriendsCount = meetings?.newFriends?.length ?? 0;
  const totalLeadersCount = meetings?.leaders?.length ?? 0;

  const totalMeetingCount =
    totalFriendsCount +
    totalMembersCount +
    totalNewFriendsCount +
    totalLeadersCount;

  const presentTotalMeetingCount =
    presentFriends +
    presentLeaders +
    presentMembers +
    presentNewFriends +
    Number(otherAttendeesCount);

  return (
    <Box
      className={css(Styles.stickyBanner)}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        padding: "8px 16px",
        flexWrap: "nowrap", // 🔥 Pura container ek line me lock rahega
        overflowX: "auto", // 🔥 Mobile par ye clean scroll allow karega bina overlap ke
        "&::-webkit-scrollbar": { height: "4px" }, // Chhota scrollbar mobile ke liye
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#D1D5DB",
          borderRadius: "4px",
        },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* ── LEFT SECTION: Filters ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        {/* 🔥 minWidth use kiya taaki component apna space le aur overlap na kare */}
        <Box sx={{ minWidth: "120px", flexShrink: 0 }}>
          <DropDownSelect
            title="Month"
            label="Month"
            selectOptions={MONTHS_OPTIONS}
            value={month}
            setValue={setMonth}
            style={{ color: Colors.black }}
            disabled
          />
        </Box>
        <Box sx={{ minWidth: "90px", flexShrink: 0 }}>
          <DropDownSelect
            title="Year"
            label="Year"
            selectOptions={YEARS_OPTIONS}
            value={year}
            setValue={setYear}
            style={{ color: Colors.black }}
            disabled
          />
        </Box>

        {/* Meeting Type */}
        <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: "#8F92A1",
              textTransform: "uppercase",
              lineHeight: 1,
              mb: "4px",
            }}
          >
            Meeting Type
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#3730A3",
              whiteSpace: "nowrap",
            }}
          >
            {selectedMeetingType}
          </Typography>
        </Box>
      </Box>

      {/* ── RIGHT SECTION: Stats ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
        }}
      >
        <StatTile
          label="Member"
          present={presentMembers}
          total={totalMembersCount}
          bg="#424874"
          textColor="#FFF"
        />
        <StatTile
          label="Leader"
          present={presentLeaders}
          total={totalLeadersCount}
          bg="#CACFD6"
          textColor="#484848"
        />
        <StatTile
          label="Friend"
          present={presentFriends}
          total={totalFriendsCount}
          bg="#A6B1E1"
          textColor="#454A61"
        />
        <StatTile
          label="New Friend"
          present={presentNewFriends}
          total={totalNewFriendsCount}
          bg="#DCD6F7"
          textColor="#484262"
        />
        <StatTile
          label="Total"
          present={presentTotalMeetingCount}
          total={totalMeetingCount}
          bg="#436390"
          textColor="#FFF"
        />

        {/* Input field width fixed so it doesn't break */}
        <Box sx={{ ml: "8px", maxWidth: "150px", flexShrink: 0 }}>
          <InputField
            label="Other Members"
            value={otherAttendeesCount}
            onChange={onChangeOtherAttendeesCount}
            disabled={isArchive}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AttendanceHeader;
